<script>
import { defineAsyncComponent } from "@common/defineAsyncComponent";
import { componentStore } from "@common/componentStore/componentStore";
import { sDs } from "@common/sDs/sDs";
import { router } from "@common/router/router";
import { library } from "@common/library/library";
import { uStore } from "@common/userStore";
import { errorHandler } from "@common/errorHandler/errorHandler";

const editorSettings = {
    showResetPwd: {
        type: 'boolean',
        label: 'Show reset password',
    },
    hideSkipLogin: {
        type: 'boolean',
        label: 'Hide skip login',
    },
    userType: {
        type: 'dropdown',
        label: 'User type',
        items: [
            { text: 'AMS', value: 'ams' },
            { text: 'RMS', value: 'rms' },
            { text: 'eCoordinator', value: 'user' },
            { text: 'Volunteer', value: 'vol' },
            { text: 'Organization', value: 'org' },
            { text: 'Client', value: 'mem' },
            { text: 'Approver', value: 'app' }
        ],
        fields: { text: 'text', value: 'value' },
    },
    messageType: {
        type: 'dropdown',
        label: 'Add a Message',
        items: [
            { text: 'From Account UDF', value: 'accountUdfId' },
            { text: 'Message Panel Component', value: 'messagePanelcId' },
            { text: 'Custom Message', value: 'customMessage' }
        ],
        fields: { text: 'text', value: 'value' },
        helpText: 'Specify the type of message to display above the login field.'
    },
    accountUdfId: {
        type: 'dropdown',
        id: 'accountUdfId',
        label: 'Message From Account UDF',
        itemsSource: 'configStore.allFieldOptions()',
        fields: {
            text: 'text', value: 'value', parentValue: 'pId', hasChildren: 'hasChild'
        },
        logic: [
            {
                record: 'self',
                valueType: 'criteria',
                criteriaRecord: 'component',
                prop: 'display',
                criteria: 'this.controls.messageType.value == "accountUdfId"'
            }
        ],
        helpText: 'Display a message populated by an account UDF above the login fields.'
    },
    customMessage: {
        type: 'text',
        label: 'Custom Message',
        id: 'customMessage',
        default: '',
        logic: [
            {
                record: 'self',
                valueType: 'criteria',
                criteriaRecord: 'component',
                prop: 'display',
                criteria: 'this.controls.messageType.value == "customMessage"'
            }
        ],
        helpText: 'Display a custom message above the login fields.'
    },
    messagePanelcId: {
        type: 'dropdown',
        label: 'Message Panel Component ID',
        id: 'messagePanelcId',
        itemsSource: 'componentStore.getList(true)',
        itemsFilter: 'this.item.cType === "messagePanel"',
        fields: { text: 'cId', value: 'cId' },
        logic: [
            {
                record: 'self',
                valueType: 'criteria',
                criteriaRecord: 'component',
                prop: 'display',
                criteria: 'this.controls.messageType.value == "messagePanelcId"'
            }
        ],
        helpText: 'A message panel is a preconfigured component that receives and displays messages according to it\'s separate component configuration.  Use this option if you already have a standard message panel set up or you want to have more advanced messaging features in your login panel.',
        message: 'Note: Using a Message Panel here, that is configured to pull User specific fields/messages, will not retrieve that data, as the user is not yet logged in.'
    },
    hideInstanceList: {
        type: 'boolean',
        label: 'Hide Instance List',
    }
};

let loginFormConfig = {
    cId: "loginForm",
    cType: "loginForm",
    cTitle: "Login Form",
    cTemplate: `<form class="login-form" v-if="config" id="loginPanelWrapper">
                    <div v-show="loginSystemMessage" id="loginSystemMessage">
                        <control ref="loginSystemMessage" name="LOGIN_SYSTEM_MASSAGE" :value="loginSystemMessage"></control>
                    </div>
                    <control v-if="repeaterMessage" name="REPEATER_MESSAGE" :value="repeaterMessage" :cssClass="'login-form-message login-form-message-main'"></control>
                    <div v-if="!userList.length" id='LoginPanel'>
                        <div v-if="!needMFA">
                            <div v-if="!isForgotPasswordForm">
                                <div v-if="config.messageType === 'accountUdfId' && accountUdfMessage">
                                    <control name="ACC_UDF_MESSAGE" :value="accountUdfMessage" :cssClass="'login-form-message'"></control>
                                </div>
                                <div v-else-if="config.messageType === 'messagePanelcId' && config.messagePanelcId">
                                    <component is="messagePanel" :cId="config.messagePanelcId"></component>
                                </div>
                                <div v-else-if="config.messageType === 'customMessage'" class="login-form-custom-message">
                                    <component :is="{template: '<div>' + config.customMessage + '</div>'}"/>
                                </div>
                                <control v-if="loginMessage" name="LOGIN_MESSAGE" :value="loginMessage" :cssClass="'login-form-message login-form-message-main'" :type="loginMessageType"></control>
                                <div class="login-form-username">
                                    <control name="LOGIN_FORM_USERNAME" :label="labelSettingsUsername.label" :required="labelSettingsUsername.required" :inlineLabel="config.inlineLabel"></control>
                                </div>
                                <div class="login-form-password">
                                    <control name="LOGIN_FORM_PASSWORD" :label="labelSettingsPassword.label" :required="labelSettingsPassword.required" :inlineLabel="config.inlineLabel"></control>
                                </div>
                                <instance-list c-id="instanceList" v-if="!config.hideInstanceList" :prop-selected-instance="instance" @update:prop-selected-instance="instance = $event"></instance-list>
                            </div>
                            <div v-else>
                                <control name="RESET_MESSAGE" :value="resetMessage" :cssClass="'login-form-message'" :type="resetMessageType"></control>
                                <div v-if="!hideResetForm">
                                    <div class="login-form-last-name">
                                        <control name="LOGIN_FORM_FIRST_LAST_NAME" :label="labelSettingsFirstLastName.label" :required="labelSettingsFirstLastName.required" :inlineLabel="config.inlineLabel"></control>
                                    </div>
                                    <div class="login-form-email">
                                        <control name="LOGIN_FORM_EMAIL" :label="labelSettingsEmail.label" :required="labelSettingsEmail.required" :inlineLabel="config.inlineLabel"></control>
                                    </div>
                                </div>
                            </div>
                            <div v-show="(enableRecaptcha || isForgotPasswordForm) && !hideResetForm" class="login-form-recaptcha">
                                <div class="g-recaptcha" data-sitekey="6LexCg4UAAAAALahYvuf4nO4_oZiScIZU7HsAO8P"></div>
                            </div>
                        </div>
                        <div v-if="needMFA && !isForgotPasswordForm">
                            <control name="LOGIN_MESSAGE" :value="mfaLoginMessage" :cssClass="'login-form-message'" :type="loginMessageType"></control>
                            <div class="login-form-mfa">
                                <label-wrapper :label-settings="labelSettingsMFA"></label-wrapper>
                                <control name="LOGIN_FORM_MFA"></control>
                            </div>
                        </div>
                        <div v-if="!isForgotPasswordForm" class="login-form-login" :style="{'display': config.showResetPwd ? 'flex' : false, 'justify-content': config.showResetPwd ? 'end' : false}">
                            <div v-if="config.showResetPwd">
                                <control name="LOGIN_FORM_RESET_BUTTON"></control>
                            </div>
                            <div>
                                <control name="LOGIN_FORM_LOGIN_BUTTON"></control>
                            </div>
                        </div>
                        <div v-else>
                            <div v-if="!hideResetForm">
                                <br/>
                                <control name="LOGIN_FORM_SUBMIT_RESET_BUTTON"></control>
                                <br/>
                            </div>
                            <control name="LOGIN_FORM_BACK_TO_LOGIN_BUTTON"></control>
                        </div>
                        <div v-if="cancelButton" class="login-form-cancel">
                            <control name="LOGIN_FORM_CANCEL_BUTTON" :label="cancelButton"></control>
                        </div>
                        <div v-if="!fromLocalhost && clientSsoEnabled" id="clientSsoLoginDiv">
                            <br/>
                            <control name="LOGIN_FORM_CLIENT_SSO_BUTTON"></control>
                        </div>
                    </div>
                    <div id="GooglePanel" v-show="googleSsoEnabled" v-if="userList.length || !fromLocalhost || (fromLocalhost && !config.hideSkipLogin)">
                        <div v-if="!userList.length">
                            <div v-if="!fromLocalhost && googleSsoEnabled" id="LOGIN_FORM_SSO_BUTTON"></div>
                            <control v-if="fromLocalhost && !config.hideSkipLogin" name="LOGIN_FORM_SKIP_BUTTON"></control>
                        </div>
                        <div v-if="userList.length">
                            Multiple users with that email:<br/>
                            <control name="USER_LIST" :items="userList"></control>
                            <control class="login-form-back-to-login-from-user-list" name="LOGIN_FORM_BACK_TO_LOGIN_FROM_USER_LIST_BUTTON"></control>
                        </div>
                    </div>
                </form>`,
    controls: [
        {
            name: 'LOGIN_FORM_USERNAME',
            type: 'string',
            hideMessage: true,
            fields: { text: 'text', value: 'value' },
            onchange: 'checkUsername',
            ref: "loginFormUsername",
        },
        {
            name: 'LOGIN_FORM_PASSWORD',
            type: 'password',
            hideMessage: true,
            fields: { text: 'text', value: 'value' },
            onchange: 'checkPassword',
            ref: "loginFormPassword",
        },
        {
            name: 'LOGIN_FORM_FIRST_LAST_NAME',
            type: 'string',
            ref: "loginFormFirstLastName",
        },
        {
            name: 'LOGIN_FORM_EMAIL',
            type: 'string',
            ref: "loginFormEmail",
        },
        {
            name: 'LOGIN_FORM_LOGIN_BUTTON',
            type: 'button',
            label: 'Login',
            onclick: 'onStandardLogin',
            cssClass: 'login-form-button login-form-login-button'
        },
        {
            name: 'LOGIN_FORM_RESET_BUTTON',
            type: 'button',
            label: 'Forgot your password?',
            onclick: 'toggleForgotPasswordForm',
            cssClass: 'forgot-password e-link'
        },
        {
            name: 'LOGIN_FORM_BACK_TO_LOGIN_BUTTON',
            type: 'button',
            label: 'Back to Login prompt',
            onclick: 'toggleForgotPasswordForm',
            cssClass: 'forgot-password e-link'
        },
        {
            name: 'LOGIN_FORM_BACK_TO_LOGIN_FROM_USER_LIST_BUTTON',
            type: 'button',
            label: 'Back to Login prompt',
            onclick: 'backToLoginPrompt',
            cssClass: 'login-form-button login-form-login-button'
        },
        {
            name: 'LOGIN_FORM_SUBMIT_RESET_BUTTON',
            type: 'button',
            label: 'Submit',
            onclick: 'onStandardReset',
            cssClass: 'login-form-button login-form-login-button'
        },
        {
            name: 'LOGIN_FORM_CLIENT_SSO_BUTTON',
            type: 'button',
            label: 'Login Via SSO',
            onclick: 'clientSsoLogin',
            cssClass: 'login-form-button login-form-sso-button'
        },
        {
            name: 'LOGIN_FORM_SKIP_BUTTON',
            type: 'button',
            label: 'Skip login',
            onclick: 'skipLogin',
            cssClass: 'login-form-button login-form-skip-button'
        },
        {
            name: 'LOGIN_FORM_CANCEL_BUTTON',
            type: 'button',
            label: 'Cancel login',
            onclick: 'cancelLogin',
            cssClass: 'login-form-button login-form-cancel-button'
        },
        {
            name: 'USER_LIST',
            type: 'list',
            onclick: 'selectUser',
            itemContentTemplate: `<div class="sso-client">{{data.clientTitle}}</div>`
        },
        {
            name: 'LOGIN_FORM_MFA',
            type: 'string',
            fields: { text: 'text', value: 'value' },
            onchange: 'checkMFA',
            ref: "loginFormMFA",
        },
        {
            name: 'LOGIN_MESSAGE',
            type: 'error',
        },
        {
            name: 'REPEATER_MESSAGE',
            type: 'info',
        },
        {
            name: 'RESET_MESSAGE',
            type: 'info',
        },
        {
            name: 'ACC_UDF_MESSAGE',
            type: 'info',
        },
        {
            name: 'LOGIN_SYSTEM_MASSAGE',
            type: 'toast',
            width: '800px',
            cssClass: 'login-system-message-warning',
            timeOut: '0',
            position: { X: "Center", Y: "Top" },
            showCloseButton: true,
            target: '#loginSystemMessage'
        }
    ]
};

let fromLocalhost = typeof FROM_LOCALHOST !== 'undefined' ? FROM_LOCALHOST : false;
let googleSsoEnabled = typeof GOOGLE_SSO_ENABLED !== 'undefined' ? GOOGLE_SSO_ENABLED : false;
let clientSsoEnabled = typeof CLIENT_SSO_ENABLED !== 'undefined' ? CLIENT_SSO_ENABLED : false;

let loginFormCmp = {
    name: 'loginForm',
    preInitControls: true,
    editorSettings: editorSettings,
    props: {
        instance: {
            default: 'dev'
        },
        userType: {
            default: 'user'
        },
        initialMessage: {
            default: ''
        },
        initialResetMessage: {
            default: 'Please enter your first and last name and email address in the box provided and click "Submit". Link that allows to reset password will be emailed to you.'
        },
        mfaMessage: {
            default: 'Enter your MFA code,</br>then click "Login".'
        },
        cancelButton: {
            default: false
        },
        labelSettingsUsername: {
            default: {
                id: 'LOGIN_FORM_USERNAME',
                label: "User ID:",
                required: true,
            }
        },
        labelSettingsPassword: {
            default: {
                id: 'LOGIN_FORM_PASSWORD',
                label: "Password:",
                required: true,
            }
        },
        labelSettingsFirstLastName: {
            default: {
                id: 'LOGIN_FORM_FIRST_LAST_NAME',
                label: "First and Last Name:",
                required: true,
            }
        },
        labelSettingsEmail: {
            default: {
                id: 'LOGIN_FORM_EMAIL',
                label: "Email Address:",
                required: true,
            }
        },
        labelSettingsMFA: {
            default: {
                id: 'LOGIN_FORM_MFA',
                label: "MFA Code:",
                required: true,
            }
        },
        enableRecaptcha: {
            default: false
        },
    },
    dataTestIds: [
    ],
    data() {
        return {
            userList: {},
            googleToken: '',
            fromLocalhost: fromLocalhost,
            googleSsoEnabled: googleSsoEnabled,
            clientSsoEnabled: clientSsoEnabled,
            needMFA: false,
            responseMessage: false,
            responseResetMessage: false,
            loginMessageType: 'info',
            resetMessageType: 'info',
            loginTimeout: false,
            isForgotPasswordForm: false,
            hideResetForm: false,
            loginSystemMessage: false,
            googleButtonRenderCount: 0,
            repeaterMessage: ''
        };
    },
    computed: {
        realUserType() {
            return this.config.userType || this.userType;
        },
        loginForm() {
            return this.config && this.config.loginForm ? this.config.loginForm : [];
        },
        loginMessage() {
            return this.responseMessage || this.initialMessage;
        },
        resetMessage() {
            return this.responseResetMessage || this.initialResetMessage;
        },
        mfaLoginMessage() {
            return this.mfaMessage;
        },
        accountUdfMessage() {
            let udfId = this.config.accountUdfId && this.config.accountUdfId.split(':::')[1];
            let result = '';
            if (udfId && this.recordStore && this.recordStore.acc && this.configStore && this.configStore.accountSettings && this.configStore.accountSettings.ACCOUNT_ID) {
                result = this.recordStore.acc[this.configStore.accountSettings.ACCOUNT_ID][udfId];
            }
            return result;
        }
    },
    components: {
        labelWrapper: defineAsyncComponent(() => import('@common/control/labelWrapper')),
        instanceList: defineAsyncComponent(() => import('@components/devDash/components/instanceList'))
    },
    methods: {
        backToLoginPrompt() {
            this.responseMessage = false;
            this.userList = {};
            this.attachGoogleHandler();
        },
        toggleForgotPasswordForm() {
            this.isForgotPasswordForm = !this.isForgotPasswordForm;
        },
        clientSsoLogin() {
            location.href = 'index.php?sso';
        },
        hideSystemMessage() {
            library.setCookie('hide_system_message', 1);
        },
        getSystemMessage() {
            this.sDs.post('er_checkSystemMessage').then((response) => {
                if (response && response.status === 'success' && response.data) {
                    if (typeof this.controls['LOGIN_SYSTEM_MASSAGE'] !== 'undefined') {
                        this.controls['LOGIN_SYSTEM_MASSAGE'].onClose = this.hideSystemMessage;
                    }
                    let tmpMessage = response.data.replaceAll('\r', '').replaceAll('\n', '').replaceAll('  ', ' ');
                    this.loginSystemMessage = '<div class="incompat-browser-icon-1">!</div><div>' + tmpMessage + '</div>';
                }
            });
        },
        onLoad() {
            library.urlParser.parse().then((response) => {
                if (response && parseInt(response.ACCOUNT_ID, 10) > 0) {
                    this.repeaterMessage = 'You are using quicklink mode, please login in ' + response.ACCOUNT_ORG + ' (' + response.ACCOUNT_ID + ') account.';
                }
            });
            this.getSystemMessage();
            let recaptchaScriptUrl = 'https://www.google.com/recaptcha/api.js';
            let scriptsWithSameSrc = document.querySelectorAll('[src="' + recaptchaScriptUrl + '"]');
            if ((this.enableRecaptcha || this.config.showResetPwd) && !scriptsWithSameSrc.length) {
                let script = document.createElement('script');
                script.src = recaptchaScriptUrl;
                document.head.appendChild(script);
            }
            this.attachGoogleHandler();
            this.focusUserIDInput();
        },
        focusUserIDInput() {
            if (this && this.controls && this.controls['LOGIN_FORM_USERNAME'] && this.controls['LOGIN_FORM_USERNAME'].id) {
                let tmpUserIdInput = document.getElementById(this.controls['LOGIN_FORM_USERNAME'].id);
                if (tmpUserIdInput && tmpUserIdInput.tagName === 'INPUT') {
                    tmpUserIdInput.focus();
                } else {
                    setTimeout(this.focusUserIDInput, 500);
                }
            }
        },
        processAction(act, param) {
            switch (act) {
                case 'trapEnter':
                    if (param && param.srcElement) {
                        if (param.srcElement.id.indexOf('LOGIN_FORM_USERNAME') !== -1) {
                            if (this && this.refs && this.refs['loginFormPassword']) {
                                this.refs['loginFormPassword'].focusIn();
                            }
                        } else if (param.srcElement.id.indexOf('LOGIN_FORM_PASSWORD') !== -1) {
                            this.processLogin();
                        }
                    }
                    break;
            }
        },
        resetResponseMessage() {
            if (this.loginTimeout) {
                this.controls['LOGIN_FORM_USERNAME'].value = "";
                this.controls['LOGIN_FORM_PASSWORD'].value = "";
                clearTimeout(this.loginTimeout);
            }
            this.loginTimeout = false;
            this.responseMessage = false;
            this.loginMessageType = 'info';
        },
        attachGoogleHandler() {
            if (this.fromLocalhost) return;
            let element = document.getElementById('LOGIN_FORM_SSO_BUTTON') ? document.getElementById('LOGIN_FORM_SSO_BUTTON') : false;
            if (element && element.tagName && element.tagName.toLocaleLowerCase() === 'div') {
                this.renderButton();
            } else {
                let self = this;
                setTimeout(function () {
                    self.attachGoogleHandler();
                }, 100); // NOSONAR
            }
        },
        checkUsername() {
            // this.resetResponseMessage();
            // TODO code this or remove
        },
        checkPassword() {
            // this.resetResponseMessage();
            // TODO code this or remove
        },
        checkMFA() {
            // TODO code this or remove
        },
        selectUser: async function (userInfo) {
            this.processLogin(this.googleToken, userInfo.clientID);
        },
        onStandardLogin: async function () {
            this.processLogin();
        },
        onStandardReset() {
            let recaptcha;
            let userName = this.controls['LOGIN_FORM_FIRST_LAST_NAME'].value;
            let email = this.controls['LOGIN_FORM_EMAIL'].value;
            if (!userName || !email) {
                let message = '';
                if (!userName) {
                    message += this.controls['LOGIN_FORM_FIRST_LAST_NAME'].label.replace(':', '') + ' cannot be empty';
                }
                if (!email) {
                    if (message) message += '<br/>';
                    message += this.controls['LOGIN_FORM_EMAIL'].label.replace(':', '') + ' cannot be empty';
                }
                if (!library.checkEmail(email)) {
                    if (message) message += '<br/>';
                    message += 'Please enter valid Email address.';
                }
                this.responseResetMessage = message;
                this.resetMessageType = 'error';
                return;
            }
            let recaptchaObj = document.getElementsByName('g-recaptcha-response');
            if (recaptchaObj && recaptchaObj.length) recaptcha = recaptchaObj[0].value;
            let sDsParams = {
                sDsParams: {
                    loadingTargetId: 'loginPanelWrapper'
                }
            };
            this.sDs.post('ec_resetUserPassword', userName, email, recaptcha, sDsParams).then((response) => {
                if (!response.status || response.status !== 'success') {
                    let message = response.error ? response.error : 'Login failed';
                    if (Array.isArray(message)) message = message[0];
                    this.resetMessageType = 'error';
                    this.responseResetMessage = message;
                } else {
                    this.resetMessageType = 'info';
                    this.responseResetMessage = 'Thank you. An email has been sent to you that contains link to set new password.';
                    this.hideResetForm = true;
                }
            });
        },
        processLogout: async function () {
            if (typeof this.$parent.processAction === 'function') {
                this.$parent.processAction('logout');
            }
        },
        processLogin: async function (googleToken, userID, loginAndLockOtherSessionsConfirmed) {
            let loginParams = {
                googleToken: googleToken,
                userID: userID,
                checkMFA: 1,
                loginCheckOtherSessions: 1,
                loginAndLockOtherSessionsConfirmed: loginAndLockOtherSessionsConfirmed ? 1 : 0,
            };
            if (!this.config.hideInstanceList) loginParams['instance'] = this.instance;
            if (this.enableRecaptcha) {
                let recaptchaObj = document.getElementsByName('g-recaptcha-response');
                if (recaptchaObj && recaptchaObj.length) loginParams.recaptcha = recaptchaObj[0].value;
            }
            if (this.needMFA) {
                loginParams['MFA'] = this.controls['LOGIN_FORM_MFA'].value;
            }
            this.responseMessage = 'Logging in... Please wait.';
            this.loginMessageType = 'info';
            if (!googleToken && (!this.controls['LOGIN_FORM_USERNAME'].value || !this.controls['LOGIN_FORM_PASSWORD'].value)) {
                let message = '';
                if (!this.controls['LOGIN_FORM_USERNAME'].value) {
                    message += this.controls['LOGIN_FORM_USERNAME'].label.replace(':', '') + ' cannot be empty';
                }
                if (!this.controls['LOGIN_FORM_PASSWORD'].value) {
                    if (message) message += '<br/>';
                    message += this.controls['LOGIN_FORM_PASSWORD'].label.replace(':', '') + ' cannot be empty';
                }
                this.responseMessage = message;
                this.loginMessageType = 'error';
                return;
            }
            let sDsParams = {
                sDsParams: {
                    loadingTargetId: 'loginPanelWrapper'
                }
            };
            let returnFields = [];
            if (this.fromLocalhost) {
                returnFields = ['sds_localSid'];
            }
            let response = await this.sDs.post("er_login", this.realUserType, this.controls['LOGIN_FORM_USERNAME'].value, this.controls['LOGIN_FORM_PASSWORD'].value, returnFields, false, false, loginParams, sDsParams);
            if (!response.status || response.status !== 'success') {
                let message = response.error ? response.error : 'Login failed';
                if (Array.isArray(message)) message = message[0]; // TODO Why is it returning an array?
                this.responseMessage = message;
                this.loginMessageType = 'error';
                this.loginTimeout = setTimeout(() => {
                    this.resetResponseMessage();
                }, 20000); // NOSONAR
            } else {
                if (response.data && response.data.sds_localSid) {
                    library.setCookie('sds_localSid', response.data.sds_localSid);
                }
                if (response.data && (response.data.userList || response.data.list)) {
                    if (typeof google !== 'undefined'
                        && typeof google.accounts !== 'undefined'
                        && typeof google.accounts.id !== 'undefined'
                        && typeof google.accounts.id.cancel === 'function')
                    {
                        setTimeout(() => {
                            google.accounts.id.cancel();
                        }, 300);
                    }
                    this.googleToken = googleToken;
                    if (response.data.userList) {
                        this.userList = response.data.userList;
                    } else {
                        let tmpUserList = [];
                        for (let j in response.data.list) {
                            if (j === 'length') continue;
                            tmpUserList.push({
                                'clientID': response.data.list[j]['CLIENT_ID'],
                                'clientTitle': response.data.list[j]['CLIENT_USER_NAME'] + ((this.realUserType === 'ams' || this.realUserType === 'rms') ? '' : ' - ' + response.data.list[j]['ACCOUNT_ORG'])
                            });
                        }
                        this.userList = tmpUserList;
                    }
                } else if (response.data && response.data.needMFA) {
                    this.needMFA = true;
                } else if (response.data && response.data.alreadyLoggedInConfirmMessage && !loginAndLockOtherSessionsConfirmed) {
                    if (typeof google !== 'undefined'
                        && typeof google.accounts !== 'undefined'
                        && typeof google.accounts.id !== 'undefined'
                        && typeof google.accounts.id.cancel === 'function')
                    {
                        setTimeout(() => {
                            google.accounts.id.cancel();
                        }, 300);
                    }
                    loginParams.loginAndLockOtherSessionsConfirmed = 1;
                    let self = this;
                    library.showDialog(response.data.alreadyLoggedInConfirmMessage,
                        function () {
                            self.processLogin(googleToken, userID, 1);
                        }, function () {
                            self.controls['LOGIN_FORM_USERNAME'].value = "";
                            self.controls['LOGIN_FORM_PASSWORD'].value = "";
                            if (typeof google !== 'undefined'
                                && typeof google.accounts !== 'undefined'
                                && typeof google.accounts.id !== 'undefined'
                                && typeof google.accounts.id.prompt === 'function')
                            {
                                setTimeout(() => {
                                    google.accounts.id.prompt();
                                }, 300);
                            }
                        },
                        { 'name': 'alreadyLoggedInConfirmMessage', 'okButtonLabel': 'Login', 'cancelButtonLabel': 'Cancel' });
                } else {
                    if (!library.isFromLocalhost() && typeof VUE_PROJECT_NAME !== 'undefined' && VUE_PROJECT_NAME === 'devDash') {
                        await library.clearAllCache();
                    }

                    uStore().inactivityLogoutFunction = this.processLogout;
                    let loggedUsers = uStore().getLoggedUsers([false, false, true]);
                    if (typeof this.$parent.processAction === 'function') {
                        this.$parent.processAction('login', loggedUsers['user']);
                    }
                    let parent = this.$parent;
                    let proccessActionExists = false;
                    while (parent) {
                        if (typeof parent.processAction === 'function') {
                            proccessActionExists = true;
                            parent.processAction('login', loggedUsers['user']);
                        }
                        parent = parent.$parent;
                    }
                    if (!proccessActionExists) {
                        router.replace({ path: "/" });
                    }
                }
            }
        },
        onGoogleLogin: async function (googleResponse) {
            // eslint-disable-next-line
            if (googleResponse && googleResponse.credential) {
                await this.processLogin(googleResponse.credential);
                google.accounts.id.prompt();
            }
        },
        onFailure(errorObject) {
            if (!errorObject || errorObject.error !== 'popup_closed_by_user') {
                library.showToast('Something wrong with Google SSO.' + (errorObject ? 'Error code: ' + errorObject.error : ''), { timeout: 10, type: 'error' });
            }
        },
        renderButton() {
            let googlePanel = document.getElementById('GooglePanel');
            if (googlePanel && googlePanel.style) googlePanel.style.display = 'block';
            let clientID = '';
            let tmpObj = document.querySelector('meta[name="google-signin-client_id"]');
            if (tmpObj) clientID = tmpObj.content;
            if (
                typeof google !== "undefined"
                && typeof google.accounts !== "undefined"
                && typeof google.accounts.id !== "undefined"
                && typeof google.accounts.id.initialize === "function"
                && clientID
            ) {
                let self = this;
                google.accounts.id.initialize({
                    client_id: clientID,
                    callback: self.onGoogleLogin,
                    cancel_on_tap_outside: false,
                    use_fedcm_for_prompt: true
                });
                let tmpSSOButton = document.getElementById('LOGIN_FORM_SSO_BUTTON');
                if (tmpSSOButton) {
                    google.accounts.id.renderButton(tmpSSOButton, {
                        'size': 'medium',
                        'width': 310
                    });
                }
                google.accounts.id.prompt();
                setTimeout(this.focusUserIDInput, 500); // NOSONAR
            } else if (this.googleButtonRenderCount < 60) { // NOSONAR
                this.googleButtonRenderCount++;
                setTimeout(this.renderButton, 1000); // NOSONAR
            }
        },
        async skipLogin() {
            if (this.fromLocalhost) {
                let loggedUsers = await uStore().getLoggedUsers();
                loggedUsers[this.realUserType] = '-1';
                uStore().loggedUsers = loggedUsers;
                if (typeof this.$parent.processAction === 'function') {
                    this.$parent.processAction('login', loggedUsers['user']);
                } else {
                    router.replace({ path: "/" });
                }
            }
        },
        async cancelLogin() {
            if (typeof this.$parent.processAction === 'function') {
                this.$parent.processAction('cancelLogin');
            }
        }
    }
};

componentStore.initComponent(loginFormCmp, loginFormConfig);
export { loginFormConfig, loginFormCmp };
export default loginFormCmp;
</script>
<style>
.sso-client {
    cursor: pointer;
    border-bottom: 1px solid #dadce0;
}
.sso-client {
    width: 330px;
    height: 30px;
    display: table-cell;
    vertical-align: middle;
    font-size: 16px;
}
.sso-client:hover {
    background:#e8f0fe;
}
.abcRioButtonIcon {
    display: none;
}
.abcRioButtonContentWrapper {
    color: #FFF;
    background: #646464 !important;
    border: 1px solid gray !important;
    width: 60px;
    font-size: 16px;
}
.abcRioButtonContents {
    font-size: 16px !important;
}
.sub-list-item-container {
    background: #ddd;
}
.login-form-custom-message {
    margin: 10px 0;
}
.login-form-message {
    margin-bottom: 10px;
    text-align: center;
    word-break: break-word;
}
.forgot-password span {
    font-weight: 400;
    color: #646464;
}
.login-form-recaptcha {
    margin-top: 10px;
}

.login-form-button {
    color: #FFF !important;
    background: #646464 !important;
    border: 1px solid gray !important;
    width: 60px;
    font-size: 16px;
}

.login-form-button:hover {
    color: #FFF !important;
}

.login-form-username [cid=labelWrapper], .login-form-password [cid=labelWrapper] {
    width: 100px;
}

.login-form-last-name [cid=labelWrapper], .login-form-email [cid=labelWrapper] {
    width: 150px;
}

[name=LOGIN_FORM_SUBMIT_RESET_BUTTON] {
    margin: 0 auto;
}

#LoginPanel, #GooglePanel {
    background-color: #EFEFEF;
    border: 1px solid #D4D4D4;
    padding: 20px;
}
.container-fluid, .container-sm, .container-md, .container-lg, .container-xl {
    padding: 0;
}

html {
    background-color: #fff;
    color: #303030;
    font-family: "Open Sans","Helvetica Neue",Helvetica,Arial,sans-serif;
    font-size: 13px;
    margin: 0;
    line-height: 1.42857143;
}

.privacy-policy a {
    text-decoration: none;
    color: #007bdb;
}
.message-item-text {
    padding: 0 !important;
}

.login-form {
    width: 350px;
    margin: 0 auto;
}

a {
    color: #007bdb;
}

.copyright {
    font-size: 11px;
    font-weight: 400;
    color: #464646;
    text-align: center;
}
#loginMessage {
    text-align: center;
    font-size: 14px;
    font-weight: 400;
    color: #464646;
}

.e-dialog .e-icon-dlg-close::before {
    content: "\e7fc" !important;
}
#loginSystemMessage {
    margin-left: -220px;
    margin-bottom: 20px;
}
.login-system-message-warning {
    font-size: 16px;
    background: white !important;
    color: #555555 !important;
    border-bottom: 1px solid #BBBBBB !important;
    min-width: 800px;
}

.e-toast-container .e-toast.login-system-message-warning .e-toast-message .e-toast-content,
.e-toast-container .e-toast.login-system-message-warning .e-toast-close-icon.e-icons
{
    color: #555555;
}

.login-system-message-warning a {
    color: #242FC9;
}
.incompat-browser-icon-1 {
    background: #E06D3C;
    font-size: 45px;
    font-weight: bold;
    line-height: 36px;
    margin: 14px;
    padding: 10px;
    color: #ffffff;
    text-align: center;
    border: 0 none;
    width: 50px;
    height: 50px;
    display: block;
    float: left;
}

[realid="LOGIN_SYSTEM_MASSAGE"] {
    position: relative !important;
    border: none !important;
}

[realid="LOGIN_SYSTEM_MASSAGE"] .e-toast {
    border: none !important;
    margin-bottom: 0px !important;
}

.e-toast .e-toast-close-icon::before {
    content: '\e7fc' !important;
    font-family: 'e-icons' !important;
}

.login-form-back-to-login-from-user-list {
    margin-top: 10px
}
</style>
